import "./SignUpComponent.scss";
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback, useMemo } from "react";
import ButtonComponent from "../../../components/ant-component/button-component/ButtonComponent";
import FormikInputComponent from "../../../components/ant-component/formik-input-component/FormikInputComponent";
import { SendOutlined } from "@ant-design/icons";
import { CommonService } from "../../../helpers";
import { ENV } from "../../../constants";
import { IAPIResponseType } from "../../../models/api.model";
import UploadImageComponent from "../../../components/upload-image-component/UploadImageComponent";
import { closeSignUpModal } from "../../../store/actions/meta.action";
import { useDispatch, useSelector } from "react-redux";
import { StateParams } from "../../../store/reducers";
import FormikSelectDropdownComponent from "../../../components/ant-component/formik-select-dropdown/FormikSelectDropdownComponent";
import { updateLoginUser } from "../../../store/actions/auth.action";

interface SignUpComponentProps {
    resumeFile?: any;
    setResumeFile?: any;
}

// Helper function to split name into first and last name
const splitName = (fullName: string): { first_name: string; last_name: string } => {
    if (!fullName) return { first_name: '', last_name: '' };
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
        return { first_name: parts[0], last_name: '' };
    }
    const first_name = parts[0];
    const last_name = parts.slice(1).join(' ');
    return { first_name, last_name };
};

const validationSchema = Yup.object({
    email: Yup.string().email("Email is invalid").required("Email is required"),
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    contact_number: Yup.string().required("Phone number is required"),
    hcp_type: Yup.string(),
    staff_specialty: Yup.string(),
});

const SignUpComponent = (props: SignUpComponentProps) => {
    const { resumeFile, setResumeFile } = props;
    const dispatch = useDispatch();
    const { hcpTypes, specialitiesMasterList } = useSelector((state: StateParams) => state.meta);

    const specialtiesFormatted = useMemo(() => {
        return specialitiesMasterList?.map((item: any) => ({
            title: item.title,
            code: item.code
        })) || []
    }, [specialitiesMasterList])

    // Get initial values from URL params (takes precedence over default values)
    const initialValues = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const urlEmail = params.get('email') || '';
        const urlName = params.get('name') || '';
        const { first_name, last_name } = splitName(urlName);
        
        return {
            email: urlEmail,
            first_name: first_name,
            last_name: last_name,
            contact_number: "",
            hcp_type: "",
            staff_specialty: ""
        };
    }, []);

    console.log('=== SignUpComponent Render ===')
    console.log('resumeFile state:', resumeFile)
    console.log('resumeFile type:', typeof resumeFile)
    console.log('resumeFile isArray:', Array.isArray(resumeFile))
    console.log('resumeFile[0]:', resumeFile?.[0])
    console.log('resumeFile[0] instanceof File:', resumeFile?.[0] instanceof File)
    console.log('==============================')


    const handleAttachment = useCallback((hcpId: string) => {
        if (!resumeFile || (Array.isArray(resumeFile) && resumeFile.length === 0)) {
            setResumeFile([])
            return;
        }

        const formData = new FormData();
        const fileToUpload = Array.isArray(resumeFile) ? resumeFile[0] : resumeFile;

        if (fileToUpload) {
            formData.append("attachment", fileToUpload);
            formData.append("attachment_type", 'Resume');
            formData.append("submission_type", 'documents_submission')
            CommonService._api.upload(ENV.API_URL + "hcp/" + hcpId + "/attachment", formData).then((resp: any) => {
                CommonService.showToast(resp.message || "Resume uploaded successfully", "success")
            }).catch((err: any) => {
                CommonService.showToast(err.error || "Failed to upload resume", 'error')
            }).finally(() => {
                setResumeFile([])
            })
        } else {
            setResumeFile([])
        }
    }, [resumeFile, setResumeFile])

    const handleSubmitResume = useCallback((values: any, { setSubmitting }: FormikHelpers<any>) => {
        setSubmitting(true);
        let payload: any = {
            ...values,
            hcp_type_specialities: values?.staff_specialty ? [values.staff_specialty] : [],
            email: values?.email?.toLowerCase().trim(),
            password: "Vars$Heroes"
        }
        
        // Call signup endpoint directly (without OTP)
        CommonService._api.post(ENV.API_URL + "ats/signUp", payload).then((response: IAPIResponseType<any>) => {
            if (response?.data?.user) {
                const user = response.data.user;
                // Store user data without token
                dispatch(updateLoginUser(user));
                
                // Upload resume if provided
                if (user?.hcp_id) {
                    handleAttachment(user.hcp_id);
                }
                
                CommonService.showToast(response?.message || "Resume submitted successfully", "success");
                dispatch(closeSignUpModal());
            }
        }).catch((error: any) => {
            CommonService.showToast(error.error || error.message || 'Error', 'error');
        }).finally(() => {
            setSubmitting(false);
        })
    }, [dispatch, handleAttachment])

    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmitResume}
                validationSchema={validationSchema}
                validateOnChange={true}
                validateOnBlur={true}
                enableReinitialize={true}
            >
                {({ values, isSubmitting }) => (
                    <Form id="add-job-basic-details">
                        <div className={'form-header-text mrg-bottom-20'}>Submit Resume</div>
                        <div>
                            <div>
                                <FormikInputComponent
                                    name="first_name"
                                    size={'large'}
                                    label={'First Name'}
                                    required={true}
                                />
                            </div>
                            <div>
                                <FormikInputComponent
                                    name="last_name"
                                    size={'large'}
                                    label={'Last Name'}
                                    required={true}
                                />
                            </div>
                            <div>
                                <FormikInputComponent
                                    name="email"
                                    size={'large'}
                                    label={'Email'}
                                    required={true}
                                />
                            </div>

                            <div>
                                <FormikInputComponent
                                    name="contact_number"
                                    size={'large'}
                                    label={'Phone Number'}
                                    placeholder={'Enter phone number'}
                                    required={true}
                                />
                            </div>
                            <div>
                                <FormikSelectDropdownComponent
                                    name="hcp_type"
                                    label="Staff Type"
                                    placeholder="Select staff type"
                                    required={false}
                                    size="large"
                                    options={Array.isArray(hcpTypes) ? hcpTypes.map((type: any) => ({
                                        title: type.title,
                                        code: type.code
                                    })) : []}
                                />
                            </div>
                            <div>
                                <FormikSelectDropdownComponent
                                    name="staff_specialty"
                                    label="Staff Specialty"
                                    placeholder="Select staff specialty (optional)"
                                    size="large"
                                    options={specialtiesFormatted}
                                />
                            </div>
                            <div className={'file-wrapper'}>
                                <UploadImageComponent
                                    isEdit={false}
                                    selectedFiles={resumeFile?.[0] || null}
                                    onClick={(file: any) => {
                                        console.log('=== UploadImageComponent onClick ===')
                                        console.log('Received file:', file)
                                        console.log('File type:', typeof file)
                                        console.log('File instanceof File:', file instanceof File)
                                        console.log('File keys:', file ? Object.keys(file) : 'null')
                                        console.log('File.name:', file?.name)
                                        console.log('File.size:', file?.size)
                                        console.log('File.type:', file?.type)
                                        console.log('====================================')
                                        // Wrap the file in an array if it's a single file
                                        setResumeFile(file ? [file] : [])
                                    }}
                                />
                            </div>
                        </div>
                        <div className={'d-flex justify-content-end mrg-top-10'}>
                            <ButtonComponent
                                type="submit"
                                color={'primary'}
                                variant={'solid'}
                                icon={<SendOutlined />}
                                loading={isSubmitting}
                                size={'large'}
                            >
                                Submit Resume
                            </ButtonComponent>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    )
}

export default SignUpComponent;
